import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  X, Download, FileText, Building2, Users, Languages, Loader2, CheckCircle2,
  Sparkles, Eye, Edit3, Save,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import MasterCounterpartyPicker from '../../../components/admin/MasterCounterpartyPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'ar', label: 'العربية', flag: '🇦🇪' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
];

const COUNTERPARTY_ROLES = [
  { value: 'party_b', label: 'Generic counterparty (Party B)' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'manager', label: 'Manager / Partner' },
];

// Placeholders auto-filled by backend from company / counterparty selection.
// We grey them out in the form to communicate "these will be auto-filled".
const AUTO_ISSUER_KEYS = new Set([
  'company_name', 'financier_name', 'financier_hq',
  'seller_name', 'seller_hq', 'seller_registration', 'seller_bank',
  'party_a_name', 'party_a_hq', 'party_a_registration', 'sender_name',
]);
const AUTO_COUNTERPARTY_KEYS = new Set([
  'party_b_name', 'party_b_hq', 'party_b_registration', 'party_b_representative',
  'manager_name', 'manager_hq', 'manager_representative',
  'buyer_name', 'buyer_hq', 'buyer_registration', 'buyer_representative',
  'supplier_name', 'recipient_name', 'party_name',
]);

/** Premium wizard to generate a PDF from a Document Template.
 *
 * Props:
 *  - template: the template object (required)
 *  - editingDoc: optional existing generated_documents record — when provided,
 *                the wizard hydrates with its saved context/body and "Save as Draft"
 *                will PATCH it instead of creating a new record.
 *  - onClose: callback
 */
const GeneratePDFWizard = ({ template, editingDoc = null, onClose }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(editingDoc ? 3 : 1);
  const [companies, setCompanies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [draftId, setDraftId] = useState(editingDoc?.id || null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [form, setForm] = useState(() => {
    if (editingDoc) {
      return {
        language: editingDoc.language || 'en',
        company_id: editingDoc.company_id || '',
        counterparty_id: editingDoc.counterparty_id || '',
        counterparty_role: editingDoc.counterparty_role || defaultRoleForType(template.type),
        include_signatures: true,
        filename: editingDoc.filename || `${template.code || 'document'}.pdf`,
        context: editingDoc.context || defaultContext(template),
        body_override: editingDoc.body_override || '',
      };
    }
    return {
      language: 'en',
      company_id: '',
      counterparty_id: '',
      counterparty_role: defaultRoleForType(template.type),
      include_signatures: true,
      filename: `${template.code || 'document'}.pdf`,
      context: defaultContext(template),
      body_override: '',     // filled on step 3 once user fetches the rendered body
    };
  });
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [bodyLoading, setBodyLoading] = useState(false);
  const [bodyTouched, setBodyTouched] = useState(!!editingDoc?.body_override);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const headers = { Authorization: `Bearer ${token}` };
        const [c, s] = await Promise.all([
          axios.get(`${API_URL}/api/procurement/companies`, { headers }),
          axios.get(`${API_URL}/api/crm/counterparties`, { headers }),
        ]);
        setCompanies(c.data || []);
        setSuppliers(s.data || []);
      } catch (_) {
        setCompanies([]); setSuppliers([]);
      }
    })();
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, []); // eslint-disable-line

  const placeholders = template.placeholders || [];
  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === form.company_id),
    [companies, form.company_id]
  );
  const selectedCounterparty = useMemo(
    () => suppliers.find((s) => s.id === form.counterparty_id),
    [suppliers, form.counterparty_id]
  );

  // Recompute auto-filled hints when selections change — purely cosmetic: the
  // backend has the final authority to fill these fields.
  const autoIssuer = useMemo(() => {
    if (!selectedCompany) return {};
    return {
      company_name: selectedCompany.legal_name || selectedCompany.trading_name,
      financier_name: selectedCompany.legal_name,
      financier_hq: selectedCompany.address,
      seller_name: selectedCompany.legal_name,
      seller_hq: selectedCompany.address,
      seller_registration: selectedCompany.trn || selectedCompany.license_number,
      party_a_name: selectedCompany.legal_name,
      party_a_hq: selectedCompany.address,
      party_a_registration: selectedCompany.trn,
      sender_name: selectedCompany.legal_name,
    };
  }, [selectedCompany]);

  const autoCounter = useMemo(() => {
    const cp = selectedCounterparty;
    if (!cp) return {};
    const name = cp.company_name || cp.name;
    const addr = cp.address || '';
    const reg = cp.trn || cp.tax_id || '';
    const contact = cp.contact_name || '';
    return {
      party_b_name: name, party_b_hq: addr, party_b_registration: reg, party_b_representative: contact,
      manager_name: name, manager_hq: addr, manager_representative: contact,
      buyer_name: name, buyer_hq: addr, buyer_registration: reg, buyer_representative: contact,
      supplier_name: name, recipient_name: name, party_name: name,
    };
  }, [selectedCounterparty]);

  const updateContext = (key, value) => {
    setForm((p) => ({ ...p, context: { ...p.context, [key]: value } }));
  };

  // Fetch the fully-rendered body (with autofill from company/counterparty) once
  // the admin reaches step 3. Admin can then freely edit to add parallel terms.
  const fetchRenderedBody = async () => {
    setBodyLoading(true);
    try {
      const payload = {
        language: form.language,
        company_id: form.company_id || null,
        counterparty_id: form.counterparty_id || null,
        counterparty_role: form.counterparty_role,
        context: form.context,
      };
      const r = await axios.post(
        `${API_URL}/api/cms/document-templates/${template.id}/render-with-autofill`,
        payload
      );
      setForm((p) => ({ ...p, body_override: r.data?.body || '' }));
      setBodyTouched(false);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Failed to load body', description: e.response?.data?.detail || e.message });
    } finally {
      setBodyLoading(false);
    }
  };

  const handleNextToBody = async () => {
    // Load body only the first time; preserve edits on subsequent navigation
    if (!form.body_override) await fetchRenderedBody();
    setStep(3);
  };

  const handlePreview = async () => {
    setLoading(true);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    try {
      const payload = {
        language: form.language,
        company_id: form.company_id || null,
        counterparty_id: form.counterparty_id || null,
        counterparty_role: form.counterparty_role,
        include_signatures: form.include_signatures,
        filename: form.filename,
        context: form.context,
        body_override: form.body_override && bodyTouched ? form.body_override : null,
      };
      const res = await axios.post(
        `${API_URL}/api/cms/document-templates/${template.id}/generate-pdf`,
        payload,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setStep(4);
      toast({ title: 'PDF ready', description: 'Preview generated — review before downloading.' });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Generation failed', description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = form.filename || `${template.code}.pdf`;
    a.click();
  };

  /**
   * Save-as-draft persists the current wizard state to `generated_documents`
   * so the admin can close the wizard and resume editing later without
   * having to regenerate the PDF preview. Re-open via the Edit button on the
   * CRM Documents list.
   */
  const handleSaveDraft = async () => {
    if (!form.company_id) {
      toast({ variant: 'destructive', title: 'Issuer required', description: 'Select the issuer company on step 1 before saving a draft.' });
      setStep(1);
      return;
    }
    setSavingDraft(true);
    try {
      if (draftId) {
        // Update existing draft
        await axios.patch(`${API_URL}/api/crm/generated-documents/${draftId}`, {
          context: form.context,
          filename: form.filename,
          body_override: form.body_override || null,
          language: form.language,
          counterparty_role: form.counterparty_role,
        });
        toast({ title: 'Draft updated', description: 'Your changes were saved.' });
      } else {
        // Create a new draft record
        const payload = {
          template_id: template.id,
          template_code: template.code,
          template_name: template.name,
          template_type: template.type,
          company_id: form.company_id,
          counterparty_id: form.counterparty_id || null,
          counterparty_role: form.counterparty_role,
          language: form.language,
          filename: form.filename,
          context: form.context,
          body_override: form.body_override || null,
          company_snapshot: selectedCompany || null,
          counterparty_snapshot: selectedCounterparty || null,
        };
        const r = await axios.post(`${API_URL}/api/crm/generated-documents/`, payload);
        setDraftId(r.data?.id);
        toast({ title: 'Draft saved', description: 'You can now close the wizard and resume editing later from CRM Documents.' });
      }
    } catch (e) {
      toast({ variant: 'destructive', title: 'Save failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSavingDraft(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="generate-pdf-wizard">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[96vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4 bg-gradient-to-r from-[#023E8A] to-[#0353A4] text-white">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5" />
            <div>
              <h3 className="text-lg font-bold flex items-center gap-2">
                Generate PDF — {template.name}
                {draftId && <span className="text-[10px] bg-orange-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wide">Editing draft</span>}
              </h3>
              <p className="text-xs text-white/80">
                {draftId ? 'Resuming an existing draft — edits are saved to the same record.' : 'Premium institutional document from CMS template.'}
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="wizard-close" className="p-1 hover:bg-white/10 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Steps */}
        <div className="border-b px-6 py-3 flex items-center gap-1 text-xs bg-gray-50 flex-wrap">
          {['Parties', 'Context', 'Review & Edit body', 'Preview & Download'].map((l, i) => (
            <React.Fragment key={l}>
              <button
                type="button"
                onClick={() => setStep(i + 1)}
                className={`px-3 py-1.5 rounded font-medium transition ${step === i + 1 ? 'bg-[#023E8A] text-white' : step > i + 1 ? 'bg-green-100 text-green-800' : 'bg-white text-gray-500 border'}`}
                data-testid={`wizard-step-${i + 1}`}
              >
                {step > i + 1 && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                {i + 1}. {l}
              </button>
              {i < 3 && <span className="text-gray-300">›</span>}
            </React.Fragment>
          ))}
        </div>

        <div className="flex-1 overflow-auto">
          {step === 1 && (
            <Step1Parties
              companies={companies}
              suppliers={suppliers}
              form={form}
              setForm={setForm}
              selectedCompany={selectedCompany}
              selectedCounterparty={selectedCounterparty}
            />
          )}
          {step === 2 && (
            <Step2Context
              placeholders={placeholders}
              context={form.context}
              autoIssuer={autoIssuer}
              autoCounter={autoCounter}
              updateContext={updateContext}
              form={form}
              setForm={setForm}
            />
          )}
          {step === 3 && (
            <Step3Body
              form={form}
              setForm={setForm}
              bodyLoading={bodyLoading}
              bodyTouched={bodyTouched}
              setBodyTouched={setBodyTouched}
              onReload={fetchRenderedBody}
            />
          )}
          {step === 4 && (
            <Step3Preview previewUrl={previewUrl} />
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-3 flex items-center justify-between bg-white">
          <div className="text-xs text-[#6C757D]">
            Template: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{template.code}</code>
            <span className="ml-3">Language: <b>{form.language.toUpperCase()}</b></span>
            {form.body_override && bodyTouched && <span className="ml-3 text-amber-700">· body edited</span>}
          </div>
          <div className="flex gap-2">
            {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} disabled={loading || savingDraft}>Back</Button>}
            {step >= 2 && (
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={savingDraft || loading}
                className="border-[#FF6B35] text-[#FF6B35] hover:bg-orange-50"
                data-testid="wizard-save-draft"
              >
                {savingDraft ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> {draftId ? 'Update draft' : 'Save as draft'}</>}
              </Button>
            )}
            {step === 1 && (
              <Button onClick={() => setStep(2)} className="bg-[#023E8A] hover:bg-[#0353A4] text-white" data-testid="wizard-next">
                Next
              </Button>
            )}
            {step === 2 && (
              <Button onClick={handleNextToBody} className="bg-[#023E8A] hover:bg-[#0353A4] text-white" data-testid="wizard-next-body">
                Next — Review body
              </Button>
            )}
            {step === 3 && (
              <Button onClick={handlePreview} disabled={loading} className="bg-[#023E8A] hover:bg-[#0353A4] text-white" data-testid="wizard-generate">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Eye className="w-4 h-4 mr-2" /> Generate preview</>}
              </Button>
            )}
            {step === 4 && previewUrl && (
              <Button onClick={handleDownload} className="bg-[#FF6B35] hover:bg-[#E5562D] text-white" data-testid="wizard-download">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ================ STEPS ================
const Step1Parties = ({ companies, suppliers, form, setForm, selectedCompany, selectedCounterparty }) => (
  <div className="p-6 space-y-6">
    <div>
      <h4 className="text-sm font-bold text-[#2B2D42] flex items-center gap-2 mb-2">
        <Building2 className="w-4 h-4 text-[#023E8A]" /> Issuer company
        <span className="text-xs font-normal text-[#6C757D]">(one of the 3 BMP companies — auto-fills issuer-side placeholders)</span>
      </h4>
      <select
        value={form.company_id}
        onChange={(e) => setForm({ ...form, company_id: e.target.value })}
        className="w-full px-3 py-2.5 border rounded-lg text-sm bg-white"
        data-testid="wizard-company-select"
      >
        <option value="">— Select issuer company —</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.legal_name || c.trading_name} {c.country ? `· ${c.country}` : ''} {c.trn ? `· TRN ${c.trn}` : ''}
          </option>
        ))}
      </select>
      {selectedCompany && (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
          <div className="font-semibold">{selectedCompany.legal_name}</div>
          <div>{selectedCompany.address}</div>
          <div className="opacity-80">TRN: {selectedCompany.trn || '—'} · License: {selectedCompany.license_number || '—'}</div>
        </div>
      )}
    </div>

    <div>
      <h4 className="text-sm font-bold text-[#2B2D42] flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-[#FF6B35]" /> Counterparty (optional)
        <span className="text-xs font-normal text-[#6C757D]">(supplier / partner / client / customer / contact / lead — auto-fills counterparty-side placeholders)</span>
      </h4>

      {/* Master Registry Picker — primary path */}
      <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
          Master Counterparty Registry
        </p>
        <MasterCounterpartyPicker
          value={form.master_counterparty_id || null}
          onSelect={(cp) => {
            if (!cp) {
              setForm({ ...form, master_counterparty_id: null });
              return;
            }
            // Pick the first linked legacy cross-reference as the counterparty_id
            const xrefs = cp.cross_references || [];
            const preferred = xrefs.find((x) => x.module === 'suppliers')
              || xrefs.find((x) => x.module === 'clients')
              || xrefs[0];
            const prefixedId = preferred ? preferred.prefixed_id : '';
            setForm({
              ...form,
              master_counterparty_id: cp.id,
              counterparty_id: prefixedId || form.counterparty_id,
            });
          }}
          placeholder="Search unified master registry…"
          testId="wizard-master-picker"
        />
      </div>

      {/* Legacy optgroup dropdown — fallback */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <select
          value={form.counterparty_id}
          onChange={(e) => setForm({ ...form, counterparty_id: e.target.value })}
          className="w-full px-3 py-2.5 border rounded-lg text-sm bg-white"
          data-testid="wizard-counterparty-select"
        >
          <option value="">— No counterparty (fill manually) —</option>
          {['supplier', 'client', 'customer', 'contact', 'lead'].map((src) => {
            const group = suppliers.filter((s) => s.source === src);
            if (group.length === 0) return null;
            const label = src === 'supplier' ? 'Suppliers / Partners'
              : src === 'client' ? 'CRM Clients'
              : src === 'customer' ? 'Finance Customers'
              : src === 'contact' ? 'CRM Contacts'
              : 'CRM Leads';
            return (
              <optgroup key={src} label={label}>
                {group.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.country ? ` · ${s.country}` : ''}{s.status ? ` · ${s.status}` : ''}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>
        <select
          value={form.counterparty_role}
          onChange={(e) => setForm({ ...form, counterparty_role: e.target.value })}
          className="w-full px-3 py-2.5 border rounded-lg text-sm bg-white"
        >
          {COUNTERPARTY_ROLES.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
        </select>
      </div>
      {selectedCounterparty && (
        <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded text-xs text-orange-900">
          <div className="flex items-center justify-between">
            <div className="font-semibold">{selectedCounterparty.name}</div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-orange-300 uppercase tracking-wide">
              {selectedCounterparty.source}
            </span>
          </div>
          <div>{selectedCounterparty.address || '—'}</div>
          <div className="opacity-80">Contact: {selectedCounterparty.contact_name || '—'} · TRN/Tax: {selectedCounterparty.trn || '—'}</div>
        </div>
      )}
    </div>

    <div>
      <h4 className="text-sm font-bold text-[#2B2D42] flex items-center gap-2 mb-2">
        <Languages className="w-4 h-4 text-[#023E8A]" /> Language & options
      </h4>
      <div className="flex items-center gap-2 flex-wrap">
        {LANGUAGES.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => setForm({ ...form, language: l.code })}
            className={`px-3 py-1.5 text-xs rounded-lg border flex items-center gap-1 ${form.language === l.code ? 'bg-[#023E8A] text-white border-[#023E8A]' : 'bg-white text-[#2B2D42] hover:bg-gray-50'}`}
            data-testid={`wizard-lang-${l.code}`}
          >
            {l.flag} {l.code.toUpperCase()}
          </button>
        ))}
        <label className="flex items-center gap-2 text-xs ml-4">
          <input type="checkbox" checked={form.include_signatures} onChange={(e) => setForm({ ...form, include_signatures: e.target.checked })} />
          Include signatures block
        </label>
      </div>
    </div>
  </div>
);

const Step2Context = ({ placeholders, context, autoIssuer, autoCounter, updateContext, form, setForm }) => {
  const groups = useMemo(() => {
    const issuer = [], counter = [], other = [];
    for (const k of placeholders) {
      if (AUTO_ISSUER_KEYS.has(k)) issuer.push(k);
      else if (AUTO_COUNTERPARTY_KEYS.has(k)) counter.push(k);
      else other.push(k);
    }
    return { issuer, counter, other };
  }, [placeholders]);

  const renderField = (k, kind) => {
    const effective = context[k] || (kind === 'issuer' ? autoIssuer[k] : kind === 'counter' ? autoCounter[k] : '');
    const isAuto = (kind === 'issuer' && !!autoIssuer[k]) || (kind === 'counter' && !!autoCounter[k]);
    const isLong = ['party_a_bank_details', 'product_description', 'product_specifications', 'delivery_location', 'inspection_body'].includes(k);
    return (
      <div key={k} className="space-y-1">
        <label className="text-[11px] text-[#6C757D] flex items-center gap-1">
          <code className="bg-gray-100 px-1 rounded font-mono">{`{${k}}`}</code>
          {isAuto && <span className="text-green-700 text-[10px]">· auto-fill available</span>}
        </label>
        {isLong ? (
          <textarea
            rows={3}
            className="w-full px-2 py-1.5 border rounded text-sm"
            value={context[k] ?? ''}
            placeholder={isAuto ? String(effective) : ''}
            onChange={(e) => updateContext(k, e.target.value)}
            data-testid={`wizard-field-${k}`}
          />
        ) : (
          <input
            className={`w-full px-2 py-1.5 border rounded text-sm ${isAuto && !context[k] ? 'bg-green-50/50' : ''}`}
            value={context[k] ?? ''}
            placeholder={isAuto ? String(effective) : ''}
            onChange={(e) => updateContext(k, e.target.value)}
            data-testid={`wizard-field-${k}`}
          />
        )}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-5">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900 flex gap-2">
        <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          Empty fields highlighted in green will be <b>auto-filled</b> by the backend using the Parties you selected on step 1.
          Anything you type here takes priority over auto-fill.
        </div>
      </div>

      {/* File name */}
      <div>
        <label className="text-xs text-[#6C757D]">Filename (for download)</label>
        <input
          className="w-full px-3 py-2 border rounded text-sm font-mono"
          value={form.filename}
          onChange={(e) => setForm({ ...form, filename: e.target.value })}
          data-testid="wizard-filename"
        />
      </div>

      {groups.issuer.length > 0 && (
        <section>
          <h4 className="text-sm font-bold text-[#2B2D42] mb-2">Issuer (auto-filled from company)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {groups.issuer.map((k) => renderField(k, 'issuer'))}
          </div>
        </section>
      )}

      {groups.counter.length > 0 && (
        <section>
          <h4 className="text-sm font-bold text-[#2B2D42] mb-2">Counterparty (auto-filled from supplier/partner)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {groups.counter.map((k) => renderField(k, 'counter'))}
          </div>
        </section>
      )}

      {groups.other.length > 0 && (
        <section>
          <h4 className="text-sm font-bold text-[#2B2D42] mb-2">Document-specific fields</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {groups.other.map((k) => renderField(k, 'other'))}
          </div>
        </section>
      )}

      {placeholders.length === 0 && (
        <p className="text-sm text-gray-500">This template has no placeholders — nothing to fill.</p>
      )}
    </div>
  );
};

const Step3Body = ({ form, setForm, bodyLoading, bodyTouched, setBodyTouched, onReload }) => (
  <div className="p-6 space-y-4">
    <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900 flex gap-2">
      <Edit3 className="w-4 h-4 shrink-0 mt-0.5" />
      <div>
        This is the <b>final body</b> that will be rendered in the PDF. Placeholders are already resolved with the Parties + Context from steps 1 and 2.
        You can freely edit paragraphs, add parallel terms or remove sections before generating. Your edits take priority over the template.
      </div>
    </div>
    <div className="flex items-center justify-between">
      <label className="text-sm font-semibold text-[#2B2D42]">Document body ({form.language.toUpperCase()})</label>
      <button
        type="button"
        onClick={onReload}
        className="text-xs text-[#023E8A] hover:underline disabled:opacity-50"
        disabled={bodyLoading}
        data-testid="wizard-reload-body"
      >
        {bodyLoading ? 'Loading…' : 'Reset body from template'}
      </button>
    </div>
    {bodyLoading ? (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded border">
        <Loader2 className="w-6 h-6 animate-spin text-[#023E8A]" />
      </div>
    ) : (
      <textarea
        value={form.body_override || ''}
        onChange={(e) => { setForm((p) => ({ ...p, body_override: e.target.value })); setBodyTouched(true); }}
        rows={22}
        className="w-full px-3 py-2 border rounded-lg text-sm font-mono leading-relaxed"
        dir={form.language === 'ar' ? 'rtl' : 'ltr'}
        data-testid="wizard-body-textarea"
      />
    )}
    {bodyTouched && (
      <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
        You edited this body. Your version will be used in the PDF and saved with the record so the same text is re-rendered if you download it later.
      </div>
    )}
  </div>
);

const Step3Preview = ({ previewUrl }) => (
  <div className="h-[70vh] bg-gray-100 flex items-center justify-center">
    {previewUrl ? (
      <iframe
        src={previewUrl}
        title="PDF preview"
        className="w-full h-full border-0"
        data-testid="wizard-preview-iframe"
      />
    ) : (
      <div className="text-[#6C757D] text-sm">Preview not ready. Go back and click "Generate preview".</div>
    )}
  </div>
);


// ================ defaults ================
function defaultRoleForType(type) {
  switch (type) {
    case 'partnership': return 'manager';
    case 'spa': return 'buyer';
    case 'mou':
    case 'loi':
    case 'nda':
    case 'ncnda': return 'party_b';
    default: return 'party_b';
  }
}

function defaultContext(template) {
  const today = new Date().toISOString().slice(0, 10);
  const plus30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const base = {
    issue_date: today, contract_date: today, effective_date: today, due_date: plus30,
    currency: 'AED',
    contract_location: 'Dubai, United Arab Emirates',
    arbitration_body: 'International Chamber of Commerce (ICC)',
    arbitration_seat: 'Paris, France',
    arbitration_language: 'English',
    arbitration_arbitrators: '1',
    governing_law: 'United Arab Emirates',
  };
  // Sensible numeric defaults for Partnership
  if (template.type === 'partnership') {
    Object.assign(base, {
      profit_margin_min: '20', profit_margin_max: '35',
      profit_split_financier: '50', profit_split_manager: '50',
      contract_years: '5', notice_days: '90', records_retention_years: '5',
    });
  }
  if (template.type === 'ncnda') {
    Object.assign(base, {
      non_circumvention_years: '2', commission_pct: '5',
      contract_years: '3', notice_days: '60',
    });
  }
  if (template.type === 'spa') {
    Object.assign(base, {
      incoterm: 'CIF', incoterm_version: 'Incoterms 2020',
      late_delivery_cap: '10', late_payment_cap: '10', confidentiality_years: '3',
      quantity_unit: 'kg',
    });
  }
  if (template.type === 'nda') {
    Object.assign(base, { duration_years: '3' });
  }
  if (template.type === 'mou') {
    Object.assign(base, {
      mou_date: today,
      mou_location: 'Dubai, United Arab Emirates',
      jv_equity_a_pct: '50',
      jv_equity_b_pct: '50',
      jv_fee_a_pct: '20',
      jv_fee_b_pct: '80',
      mou_validity_months: '12',
      governing_country: 'United Arab Emirates',
      negotiation_days: '30',
      party_a_representative_role: 'Chief Executive Officer',
      party_b_representative_role: 'Chief Executive Officer',
    });
  }
  return base;
}

export default GeneratePDFWizard;
